/* IC Jewel Appraisal — sections/ic-jewel-appraisal.liquid */
(() => {
  const SUB_KEY = 'icja-subscribed';
  const EMAIL_KEY = 'icja-email';
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));
  const store = {
    get(k, fallback) { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : fallback; } catch (e) { return fallback; } },
    set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* private mode */ } },
  };

  class JewelAppraisal {
    constructor(root) {
      this.root = root;
      this.$ = (sel) => root.querySelector(sel);
      this.cache = new Map();
      this.jewel = null;
      this.subState = 'no'; // yes | challenge | no
      this.fmt = new Intl.NumberFormat('en-GB', { style: 'currency', currency: root.dataset.currency || 'GBP' });
      this.bind();

      const emailInput = this.$('[data-code-form] [name="email"]');
      emailInput.value = store.get(EMAIL_KEY, '') || '';
      const preset = new URLSearchParams(location.search).get('code');
      if (preset) {
        this.$('[data-code-form] [name="code"]').value = preset;
        if (emailInput.value) this.onCode(); else emailInput.focus();
      }
    }

    bind() {
      this.$('[data-code-form]').addEventListener('submit', (e) => { e.preventDefault(); this.onCode(); });
      this.$('[data-again]').addEventListener('click', () => this.reset());
      this.root.querySelectorAll('[data-share-to]').forEach((b) => b.addEventListener('click', () => this.shareTo(b.dataset.shareTo)));
      this.$('[data-confirm-sub]').addEventListener('click', () => this.confirmSubscription());
      const codeBtn = this.$('[data-discount-code]');
      if (codeBtn) codeBtn.addEventListener('click', () => this.copy(codeBtn.dataset.code, codeBtn));
      this.root.querySelectorAll('[data-add]').forEach((b) => b.addEventListener('click', () => this.addToCart(b)));
    }

    show(step) {
      this.root.querySelectorAll('[data-step]').forEach((el) => {
        const on = el.dataset.step === step;
        el.hidden = !on;
        el.classList.toggle('is-active', on);
      });
    }

    error(sel, msg) {
      const el = this.$(sel);
      el.textContent = msg || '';
      el.hidden = !msg;
      if (msg) {
        this.root.classList.remove('is-shake');
        void this.root.offsetWidth;
        this.root.classList.add('is-shake');
        if (navigator.vibrate) navigator.vibrate(80);
      }
    }

    /* ---------- Step 1: email + code ---------- */
    validEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim()); }

    async onCode() {
      const form = this.$('[data-code-form]');
      const emailInput = form.querySelector('[name="email"]');
      const codeInput = form.querySelector('[name="code"]');
      const email = emailInput.value.trim();
      const code = codeInput.value.trim();
      if (!this.validEmail(email)) { this.error('[data-code-error]', 'Enter your email so we can record your appraisal.'); emailInput.focus(); return; }
      if (!code) { this.error('[data-code-error]', 'Enter the code printed on your jewel’s tag.'); codeInput.focus(); return; }
      this.email = email;
      store.set(EMAIL_KEY, email);
      const consent = form.querySelector('[data-consent]').checked;

      const btn = form.querySelector('button[type="submit"]');
      btn.disabled = true;
      this.root.classList.add('is-busy');
      this.error('[data-code-error]', '');
      try {
        const jewel = await this.lookup(code);
        if (!jewel.found) {
          this.error('[data-code-error]', this.root.dataset.notFound);
          this.report('Not found', { code });
          return;
        }
        this.jewel = jewel;
        // Already subscribed with this email on this device: don't re-subscribe (or re-send to Spoks).
        const known = store.get(SUB_KEY, false);
        if (known === true || known === email) {
          this.subState = 'yes';
          this.appraise(null);
        } else {
          this.subState = 'no';
          this.appraise(consent ? email : null);
        }
      } catch (err) {
        this.error('[data-code-error]', 'We couldn’t reach the vault just now. Please try again.');
      } finally {
        btn.disabled = false;
        this.root.classList.remove('is-busy');
      }
    }

    async lookup(code) {
      const key = code.toLowerCase();
      if (this.cache.has(key)) return this.cache.get(key);
      const url = `${this.root.dataset.lookupUrl}?q=${encodeURIComponent(code)}&type=page&section_id=ic-jewel-lookup`;
      const res = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!res.ok) throw new Error(`Lookup failed: ${res.status}`);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const data = JSON.parse(doc.querySelector('[data-ic-jewel]').textContent);
      if (data.found) data.value = parseFloat(data.value) || 0;
      this.cache.set(key, data);
      return data;
    }

    async subscribe(email) {
      const form = this.$('[data-news-form]');
      form.querySelector('[name="contact[email]"]').value = email;
      form.querySelector('[name="contact[tags]"]').value = [this.root.dataset.baseTags, this.jewel && `jewel-${this.handle(this.jewel.code)}`].filter(Boolean).join(', ');
      try {
        const res = await fetch(form.action, { method: 'POST', body: new FormData(form), credentials: 'same-origin' });
        this.subState = /\/challenge/.test(res.url) ? 'challenge' : res.ok ? 'yes' : 'no';
      } catch (e) {
        this.subState = 'no';
      }
      if (this.subState === 'yes') store.set(SUB_KEY, email);
      // Opted in during this visit: the sheet forwards only these to Spoks.
      this.freshSignup = this.subState !== 'no';
      return this.subState;
    }

    handle(code) { return String(code).toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''); }

    /* ---------- Reporting (Google Sheet via Apps Script web app) ---------- */
    report(result, extra = {}) {
      const url = this.root.dataset.reportUrl;
      if (!url) return;
      const j = this.jewel || {};
      const newsletter = { yes: 'Subscribed', challenge: 'Pending captcha', no: 'No' }[this.subState];
      const body = JSON.stringify({
        key: this.root.dataset.reportKey,
        result,
        code: j.code || '',
        value: j.value ?? '',
        material: j.material || '',
        email: this.email || '',
        newsletter: result === 'Not found' ? '' : newsletter,
        new_signup: !!this.freshSignup,
        page: location.href,
        ...extra,
      });
      this.freshSignup = false;
      try {
        if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }))) return;
        fetch(url, { method: 'POST', mode: 'no-cors', keepalive: true, headers: { 'Content-Type': 'text/plain' }, body });
      } catch (e) { /* reporting must never break the reveal */ }
    }

    confirmSubscription() {
      const form = this.$('[data-news-form]');
      form.target = '_blank';
      form.submit();
      store.set(SUB_KEY, this.email || true);
      this.$('[data-confirm-msg]').hidden = true;
    }

    /* ---------- Step 3: suspense ---------- */
    async appraise(email) {
      this.show('appraising');
      this.root.classList.add('is-busy');
      const lines = [...this.$('[data-suspense]').content.querySelectorAll('span')].map((s) => s.textContent);
      const status = this.$('[data-status]');
      let i = 0;
      status.textContent = lines[0];
      const timer = setInterval(() => { i = (i + 1) % lines.length; status.textContent = lines[i]; }, 850);

      await Promise.all([email ? this.subscribe(email) : null, wait(REDUCED ? 400 : 2600)]);
      clearInterval(timer);
      this.root.classList.remove('is-busy');
      this.startReveal();
    }

    /* ---------- Step 4: reveal ---------- */
    startReveal() {
      const j = this.jewel;
      this.root.classList.remove('is-revealed');
      this.$('[data-value]').textContent = this.fmt.format(0);
      this.$('[data-material]').textContent = j.material;
      this.$('[data-code-display]').textContent = `Code ${j.code}`;
      this.$('[data-after]').hidden = false;
      this.show('reveal');
      this.root.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'start' });
      void this.root.offsetWidth; // restart the pop animation on repeat reveals
      this.root.classList.add('is-revealed');
      // Confetti bursts the moment the count-up lands on the final value.
      this.countUp(this.$('[data-value]'), j.value, () => this.confetti());
      if (navigator.vibrate) navigator.vibrate([40, 60, 120]);
      this.report('Revealed');
      this.prepareImages();
      this.showSubState();
    }

    showSubState() {
      const subbed = this.subState === 'yes';
      this.$('[data-subscribed-msg]').hidden = !subbed;
      if (this.subState !== 'no') this.showDiscount();
      this.$('[data-confirm-msg]').hidden = this.subState !== 'challenge';
    }

    /* ---------- Unique discount code (created by the Google Sheet script) ---------- */
    async showDiscount() {
      const box = this.$('[data-discount]');
      const url = this.root.dataset.reportUrl;
      if (!box || this.root.dataset.uniqueDiscount !== 'true' || !url || !this.email || !this.jewel) return;
      const btn = this.$('[data-discount-code]');
      const loading = this.$('[data-discount-loading]');
      box.hidden = false;
      try {
        if (!this.discountCode || this.discountFor !== this.email) {
          loading.hidden = false;
          btn.hidden = true;
          const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain' },
            body: JSON.stringify({ key: this.root.dataset.reportKey, action: 'discount', email: this.email, code: this.jewel.code }),
          });
          const data = await res.json();
          if (!data.code) throw new Error(data.error || 'No code');
          this.discountCode = data.code;
          this.discountFor = this.email;
        }
        btn.textContent = this.discountCode;
        btn.dataset.code = this.discountCode;
        btn.hidden = false;
        loading.hidden = true;
      } catch (e) {
        box.hidden = true; // never show a broken promise
      }
    }

    /* ---------- Upsell ---------- */
    async addToCart(btn) {
      const label = btn.textContent;
      btn.disabled = true;
      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const res = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(btn.dataset.add), quantity: 1 }] }),
        });
        if (!res.ok) throw new Error((await res.json()).description || 'Could not add');
        btn.textContent = '✓';
        this.$('[data-added]').hidden = false;
        const cart = await (await fetch(`${root}cart.js`)).json();
        document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; });
        if (window.halo && typeof window.halo.updateSidebarCart === 'function') {
          window.halo.updateSidebarCart(cart);
          document.body.classList.add('cart-sidebar-show');
        }
        this.report('Added to basket', { material: btn.closest('.icja__product')?.querySelector('.icja__product-title')?.textContent.trim() || '' });
      } catch (e) {
        btn.textContent = e.message.length < 40 ? e.message : 'Try again';
      }
      setTimeout(() => { btn.textContent = label; btn.disabled = false; }, 2200);
    }

    countUp(el, to, done) {
      if (REDUCED) { el.textContent = this.fmt.format(to); if (done) done(); return; }
      const start = performance.now(), dur = 1200;
      const tick = (now) => {
        const t = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - t, 3);
        el.textContent = this.fmt.format(to * eased);
        if (t < 1) requestAnimationFrame(tick);
        else if (done) done();
      };
      requestAnimationFrame(tick);
    }

    /* ---------- Confetti (gem-shaped) ---------- */
    confetti() {
      if (REDUCED) return;
      const canvas = this.root.querySelector('.icja-confetti');
      const ctx = canvas.getContext('2d');
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const W = innerWidth, H = innerHeight;
      canvas.width = W * dpr; canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const colors = ['#b8914a', '#e9d3a0', '#f7c6d9', '#9ad0f5', '#c7a6f2', '#ffffff', '#d4145a'];
      const count = 200;
      const parts = [];
      const burst = (x, y, n) => {
        for (let i = 0; i < n; i++) {
          const a = Math.random() * Math.PI * 2, v = 4 + Math.random() * 9;
          parts.push({ x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v - 6, s: 5 + Math.random() * 7, r: Math.random() * 6, vr: (Math.random() - .5) * .3, c: colors[i % colors.length], life: 1 });
        }
      };
      burst(W / 2, H * .45, count);
      setTimeout(() => { burst(W * .1, H * .75, count / 2); burst(W * .9, H * .75, count / 2); }, 350);
      setTimeout(() => burst(W / 2, H * .3, count), 900);
      setTimeout(() => { burst(W * .2, H * .35, count / 2); burst(W * .8, H * .35, count / 2); }, 1300);

      cancelAnimationFrame(this.confettiRaf);
      const frame = () => {
        ctx.clearRect(0, 0, W, H);
        for (const p of parts) {
          p.vy += .22; p.vx *= .985; p.x += p.vx; p.y += p.vy; p.r += p.vr; p.life -= .006;
          ctx.save(); ctx.globalAlpha = Math.max(0, p.life); ctx.translate(p.x, p.y); ctx.rotate(p.r); ctx.fillStyle = p.c;
          ctx.beginPath(); ctx.moveTo(0, -p.s); ctx.lineTo(p.s * .6, -p.s * .2); ctx.lineTo(0, p.s); ctx.lineTo(-p.s * .6, -p.s * .2); ctx.closePath(); ctx.fill();
          ctx.restore();
        }
        for (let i = parts.length - 1; i >= 0; i--) if (parts[i].life <= 0 || parts[i].y > H + 40) parts.splice(i, 1);
        if (parts.length) this.confettiRaf = requestAnimationFrame(frame); else ctx.clearRect(0, 0, W, H);
      };
      this.confettiRaf = requestAnimationFrame(frame);
    }

    /* ---------- Share ---------- */
    caption() {
      const j = this.jewel;
      return `I just found a ${j.material || 'jewel'} worth an estimated ${this.fmt.format(j.value)} in my ${this.root.dataset.brand} candle! 💎✨ ${this.root.dataset.hashtag}`;
    }

    // Images are drawn right after the reveal: iPhones only allow sharing straight from a tap,
    // so the files must already exist when the button is pressed.
    prepareImages() {
      this.images = null;
      const jewel = this.jewel;
      Promise.all([this.renderImage(1920), this.renderImage(1350)]).then(([story, post]) => {
        if (this.jewel !== jewel) return;
        this.images = { story: this.toFile(story, 'story'), post: this.toFile(post, 'post') };
      });
    }

    toFile(blob, kind) {
      return new File([blob], `imperial-candles-jewel-${String(this.jewel.code).toLowerCase()}-${kind}.png`, { type: 'image/png' });
    }

    // 1920 tall = Instagram/TikTok story, 1350 tall = feed post.
    async renderImage(height) {
      const j = this.jewel;
      const serif = getComputedStyle(this.root).getPropertyValue('--icja-serif').trim() || 'Georgia, serif';
      if (document.fonts && document.fonts.ready) await document.fonts.ready;
      const W = 1080, H = height, oy = (H - 1350) / 2;
      const c = document.createElement('canvas');
      c.width = W; c.height = H;
      const x = c.getContext('2d');
      const gold = '#b8914a', ink = '#1c1a17', muted = '#7a7166';
      x.fillStyle = '#f6f1e9'; x.fillRect(0, 0, W, H);
      x.strokeStyle = gold; x.lineWidth = 4; x.strokeRect(50, 50, W - 100, H - 100);
      x.lineWidth = 1.5; x.strokeRect(70, 70, W - 140, H - 140);
      x.textAlign = 'center';
      x.translate(0, oy);
      x.fillStyle = ink; x.font = `600 34px ${serif}`;
      x.fillText(this.root.dataset.brand.toUpperCase().split('').join(' '), 540, 190);
      x.fillStyle = gold; x.font = `600 24px system-ui, sans-serif`;
      x.fillText('M Y   J E W E L   R E V E A L', 540, 250);
      x.save(); x.translate(540, 390); x.strokeStyle = gold; x.lineWidth = 5; x.lineJoin = 'round';
      x.beginPath(); x.moveTo(-70, -60); x.lineTo(70, -60); x.lineTo(130, 0); x.lineTo(0, 110); x.lineTo(-130, 0); x.closePath(); x.stroke();
      x.beginPath(); x.moveTo(-130, 0); x.lineTo(130, 0); x.moveTo(-70, -60); x.lineTo(-40, 0); x.lineTo(0, 110); x.lineTo(40, 0); x.lineTo(70, -60); x.stroke();
      x.restore();
      x.fillStyle = ink; x.font = `700 170px ${serif}`; x.fillText(this.fmt.format(j.value), 540, 720);
      x.fillStyle = muted; x.font = `500 26px system-ui, sans-serif`; x.fillText('E S T I M A T E D   R R P', 540, 780);
      x.fillStyle = ink; x.font = `italic 44px ${serif}`;
      this.wrap(x, j.material || '', 540, 890, 820, 56);
      x.fillStyle = muted; x.font = `500 26px ui-monospace, Menlo, monospace`;
      x.fillText(`CODE ${String(j.code).toUpperCase()}  ·  ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`, 540, 1100);
      x.fillStyle = gold; x.font = `700 44px system-ui, sans-serif`; x.fillText(this.root.dataset.hashtag, 540, 1180);
      x.fillStyle = muted; x.font = `400 20px system-ui, sans-serif`;
      x.fillText('Estimated retail value for guidance only. Not a professional valuation.', 540, 1245);
      return new Promise((r) => c.toBlob(r, 'image/png'));
    }

    wrap(ctx, text, cx, y, max, lh) {
      const words = text.split(' ');
      let line = '', lines = [];
      for (const w of words) {
        const test = line ? `${line} ${w}` : w;
        if (ctx.measureText(test).width > max && line) { lines.push(line); line = w; } else line = test;
      }
      lines.push(line);
      lines.slice(0, 3).forEach((l, i) => ctx.fillText(l, cx, y + i * lh));
    }

    async getImages() {
      if (!this.images) {
        const [story, post] = await Promise.all([this.renderImage(1920), this.renderImage(1350)]);
        this.images = { story: this.toFile(story, 'story'), post: this.toFile(post, 'post') };
      }
      return this.images;
    }

    // Instagram and TikTok can't be posted to from a website: on phones we hand the image to the
    // share sheet (the person picks Instagram/TikTok/Facebook). share() must be the first thing
    // the tap does: iPhones block it if anything else (e.g. copying to the clipboard) runs first.
    async shareTo(network) {
      const caption = this.caption();
      const d = this.root.dataset;
      const images = this.images || (await this.getImages());
      const file = network === 'facebook' ? images.post : images.story;

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], text: caption });
          this.toast(d.tipPhone);
          return;
        } catch (e) {
          if (e && e.name === 'AbortError') return;
        }
      }
      // Computers (and phones without file sharing): save the image and copy the caption.
      if (navigator.clipboard) navigator.clipboard.writeText(caption).catch(() => {});
      if (network === 'facebook') {
        const url = location.origin + location.pathname;
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank', 'noopener,width=620,height=640');
        return;
      }
      this.save(file);
      this.toast(network === 'tiktok' ? d.tipTiktok : d.tipInstagram);
    }

    save(file) {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(file);
      a.download = file.name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    }

    toast(msg) {
      const el = this.$('[data-share-toast]');
      if (!el || !msg) return;
      el.textContent = msg;
      el.hidden = false;
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => { el.hidden = true; }, 9000);
    }

    async copy(text, btn) {
      try { await navigator.clipboard.writeText(text); } catch (e) { return; }
      if (!btn) return;
      const old = btn.textContent;
      btn.textContent = 'Copied ✓';
      setTimeout(() => { btn.textContent = old; }, 1600);
    }

    reset() {
      this.jewel = null;
      if (!store.get(SUB_KEY, false) && this.subState !== 'yes') this.subState = 'no';
      const code = this.$('[data-code-form] [name="code"]');
      code.value = '';
      this.error('[data-code-error]', '');
      this.show('code');
      code.focus();
    }
  }

  const init = () => document.querySelectorAll('[data-icja]').forEach((el) => {
    if (!el.icja) el.icja = new JewelAppraisal(el);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();
